import React, { useEffect, useState } from 'react'
import { PrismicRichText } from '@prismicio/react'
import { Maybe, PrismicStructuredTextType } from 'src/typings/generated/graphql'
import BertholdTitle from 'src/components/atoms/bertholdTitle'
import RetroIconsStrip from 'src/components/atoms/retroIconsStrip'
import ArtOfChickenMeal from 'src/images/ArtofChickenMeal.webp'
import RightArrowIcon from 'src/images/svgs/icons/RightArrowIcon.svg'
import * as GlobalStyles from 'src/styles/global.module.scss'
import CustomLink from 'src/utils/customLink'
import { generateRandomId } from 'src/utils/domHelper'
import { ArtOfAPerfectMealContent } from './artOfAPerfectMeal'
import * as Styles from './artOfAPerfectMealV2.module.scss'

type IngredientDetails = {
  title: string
  details: Maybe<PrismicStructuredTextType> | undefined
  markerClass: string
}

type Props = {
  content: ArtOfAPerfectMealContent
}

const ArtOfAPerfectMealV2 = ({ content }: Props) => {
  const {
    anchor = generateRandomId(),
    headline = 'THE ART OF THE PERFECT CHICKEN FINGER MEAL',
    link,
    linkText = '',
    sauceTitle = '',
    sauceDetails,
    slawTitle = '',
    slawDetails,
    drinkTitle = '',
    drinkDetails,
    friesTitle = '',
    friesDetails,
    toastTitle = '',
    toastDetails,
    chickenTitle = '',
    chickenDetails,
  } = content
  const [activeIngredientIndex, setActiveIngredientIndex] = useState(0)
  const [timerRunning, setTimerRunning] = useState(true)
  const timerInterval = 10000

  const ingredients: IngredientDetails[] = [
    {
      title: chickenTitle,
      details: chickenDetails,
      markerClass: Styles.ingredient1,
    },
    {
      title: sauceTitle,
      details: sauceDetails,
      markerClass: Styles.ingredient2,
    },
    {
      title: slawTitle,
      details: slawDetails,
      markerClass: Styles.ingredient3,
    },
    {
      title: friesTitle,
      details: friesDetails,
      markerClass: Styles.ingredient4,
    },
    {
      title: toastTitle,
      details: toastDetails,
      markerClass: Styles.ingredient5,
    },
    {
      title: drinkTitle,
      details: drinkDetails,
      markerClass: Styles.ingredient6,
    },
  ]

  useEffect(() => {
    let interval: NodeJS.Timer
    if (timerRunning)
      interval = setInterval(() => {
        const nextItem =
          activeIngredientIndex < ingredients.length - 1
            ? activeIngredientIndex + 1
            : 0
        setActiveIngredientIndex(nextItem)
      }, timerInterval)
    return () => clearInterval(interval)
  }, [timerRunning, activeIngredientIndex, ingredients.length])

  const selectIngredient = (index: number) => {
    setTimerRunning(false)
    setActiveIngredientIndex(index)
  }

  return (
    <section id={anchor} className={Styles.artOfAPerfectMealV2}>
      <div className={Styles.retroIconsWrapper}>
        <RetroIconsStrip />
      </div>
      <div
        className={Styles.content}
        role="region"
        aria-label="Map of Raising Cane's Chicken Finger meal"
      >
        <div className={Styles.mealImageWrapper}>
          <img
            src={ArtOfChickenMeal}
            alt="Raising Cane's Chicken Finger meal"
          />
          {ingredients.map((ingredient, index) => (
            <button
              key={ingredient.markerClass}
              type="button"
              className={`${Styles.ingredientButton} ${
                ingredient.markerClass
              } ${activeIngredientIndex === index ? Styles.active : ''} ${
                Styles.selectDisable
              }`}
              onClick={() => selectIngredient(index)}
            >
              <span>{String(index + 1).padStart(2, '0')}</span>
            </button>
          ))}
        </div>
        <div className={Styles.ingredientDetailsWrapper}>
          <button
            className={Styles.previousButton}
            type="button"
            onClick={() =>
              selectIngredient(
                activeIngredientIndex > 0
                  ? activeIngredientIndex - 1
                  : ingredients.length - 1
              )
            }
            aria-label="previous ingredient"
          >
            <span className={Styles.arrow} aria-hidden="true">
              <RightArrowIcon aria-hidden="true" />
            </span>
          </button>
          <div className={Styles.detailsContent}>
            <h2 className={Styles.headline}>
              {BertholdTitle({ text: headline })}
            </h2>
            <div
              key={`details-${activeIngredientIndex}`}
              className={Styles.ingredientDetails}
            >
              <p className={Styles.activeIngredientIndex}>
                {String(activeIngredientIndex + 1).padStart(2, '0')}
              </p>
              <h3 className={Styles.title}>
                {BertholdTitle({
                  text: ingredients[activeIngredientIndex].title,
                })}
              </h3>
              <PrismicRichText
                field={ingredients[activeIngredientIndex].details?.richText}
                components={{
                  hyperlink: ({ node, children }: any) =>
                    CustomLink({ link: node.data, children }),
                }}
              />
              <CustomLink
                className={`${GlobalStyles.redButtonResponsive} ${Styles.detailsButton} uk-override`}
                link={link}
              >
                {linkText}
              </CustomLink>
            </div>
          </div>
          <button
            className={Styles.nextButton}
            type="button"
            onClick={() =>
              selectIngredient(
                activeIngredientIndex < ingredients.length - 1
                  ? activeIngredientIndex + 1
                  : 0
              )
            }
            aria-label="next ingredient"
          >
            <span className={Styles.arrow} aria-hidden="true">
              <RightArrowIcon aria-hidden="true" />
            </span>
          </button>
        </div>
      </div>
    </section>
  )
}

export default ArtOfAPerfectMealV2
